import { action, observable, reaction, makeObservable } from 'mobx';
import {Disposers} from '../../../helpers';
import {MovableState, Rect, XY} from '../movable/models';
import {
  DragDropContextProps,
  DraggableId,
  DraggableState,
  DragStart,
  DragUpdate,
  DroppableId
} from '../models';
import {DroppableStore} from '../droppable/DroppableStore';
import {DraggableStore} from '../draggable/DraggableStore';

interface DragDropContextPropsHolder {
  readonly props: Readonly<DragDropContextProps>;
}

let instanceIdCounter = 0;

function moveToFront<T>(array: T[], predicate: (t: T) => boolean) {
  let index = array.findIndex(predicate);
  if (index === -1)
    return;
  const value = array[index];
  array.splice(index, 1);
  array.splice(0, 0, value);
}

function isInsideRect(point: XY, rect: Rect) {
  return (rect.left <= point.x && point.x <= rect.right &&
    rect.top <= point.y && point.y <= rect.bottom);
}

function findDraggingOverDesc(droppableStores: DroppableStore[],
                              draggableId: DraggableId,
                              source: DroppableId,
                              canDropOn: ((draggableId: DraggableId, source: DroppableId, destination: DroppableId) => boolean),
                              state: Readonly<MovableState>): DroppableStore | null {
  const scroll: XY = {x: window.scrollX, y: window.scrollY};

  for (let droppable of droppableStores) {
    if (droppable.disposed)
      continue;
    if (droppable.droppableRef.current === null)
      continue;
    if (!canDropOn(draggableId, source, droppable.droppableId))
      continue;

    const element = droppable.droppableRef.current as HTMLElement;
    const clientRect = element.getBoundingClientRect();
    const bounds: Rect = {
      top: scroll.y + clientRect.top,
      left: scroll.x + clientRect.left,
      bottom: scroll.y + clientRect.bottom,
      right: scroll.x + clientRect.right
    };
    const current = state.currentPosition;

    if (!isInsideRect(current, bounds))
      continue;
    moveToFront(droppableStores,
      (x) => x.instanceId === droppable.instanceId);

    let draggingOverChild = findDraggingOverDesc(droppable.droppableStores,
      draggableId, source, canDropOn, state);

    return draggingOverChild === null ? droppable : draggingOverChild;
  }

  return null;
}


function forEachDroppableStoreDesc(droppableStores: DroppableStore[],
                                   action: (store: DroppableStore) => void) {
  for (let store of droppableStores) {
    action(store);
    forEachDroppableStoreDesc(store.droppableStores, action);
  }
}

function draggableStateFromMovableState(state: Readonly<MovableState>): DraggableState {
  return {
    draggableId: state.movableId,
    currentPosition: state.currentPosition,
    draggableSize: state.draggableSize,
    dragStart: state.dragStart,
    inset: state.inset,
    mirrorPosition: state.inset,
    cancel: state.cancel,
  };
}

export class DragDropContextStore {
  readonly instanceId: number;

  readonly disposers = new Disposers();

  @observable.shallow
  readonly droppableStores: DroppableStore[];

  constructor(readonly component: DragDropContextPropsHolder) {
    makeObservable(this);
    this.instanceId = instanceIdCounter++;
    this.droppableStores = [];

    this.setup();
  }

  private setup() {
    this.disposers.add(reaction(
      () => this.droppableStores.map(x => x.disposed),
      () => {
        for (let store of this.droppableStores)
          if (store.disposed)
            this.removeDroppableStore(store);
      }));
  }

  @action
  addDroppableStore = (store: DroppableStore) => {
    let index = this.droppableStores.findIndex((x) => x.instanceId === store.instanceId);
    if (index !== -1)
      return;
    this.droppableStores.push(store);
  };

  @action
  removeDroppableStore = (store: DroppableStore) => {
    let index = this.droppableStores.findIndex((x) => x.instanceId === store.instanceId);
    if (index === -1)
      return;
    this.droppableStores.splice(index, 1);
  };

  @action
  onDragStart = (draggable: DraggableStore,
                 droppable: DroppableStore,
                 state: Readonly<MovableState>): void => {
    const draggableId = draggable.draggableId;
    const droppableId = droppable.droppableId;

    forEachDroppableStoreDesc(this.droppableStores,
      droppable => {
        droppable.setDraggingOverWith(null);
        droppable.setDraggingFromThisWith(null);
      });
    droppable.setDraggingFromThisWith(draggableId);
    droppable.setDraggingOverWith(draggableId);

    const onDragStartCallback = this.component.props.onDragStart;
    if (typeof onDragStartCallback !== 'function')
      return;

    const start: DragStart = {
      draggableId: draggableId,
      fromDroppableId: droppableId,
      state: draggableStateFromMovableState(state)
    };
    onDragStartCallback(start);
  };

  @action
  onDragMove = (draggable: DraggableStore,
                droppable: DroppableStore,
                state: Readonly<MovableState>): void => {
    const draggableId = draggable.draggableId;
    const droppableId = droppable.droppableId;

    forEachDroppableStoreDesc(this.droppableStores,
      droppableStore => droppableStore.setDraggingOverWith(null));

    const draggingOver = this.findDraggingOver(draggableId, droppableId, state);
    if (draggingOver !== null)
      draggingOver.setDraggingOverWith(draggableId);

    const onDragUpdateCallback = this.component.props.onDragUpdate;
    if (typeof onDragUpdateCallback !== 'function')
      return;

    const update: DragUpdate = {
      draggableId: draggableId,
      fromDroppableId: droppableId,
      toDroppableId: draggingOver === null ? null : draggingOver.droppableId,
      state: draggableStateFromMovableState(state)
    };
    onDragUpdateCallback(update);
  };

  @action
  onDragCancel = (draggable: DraggableStore,
                  droppable: DroppableStore,
                  state: Readonly<MovableState>): void => {
    forEachDroppableStoreDesc(this.droppableStores, droppableStore => {
      droppableStore.setDraggingOverWith(null);
      droppableStore.setDraggingFromThisWith(null);
    });

    const onDragCancelCallback = this.component.props.onDragCancel;
    if (typeof onDragCancelCallback !== 'function')
      return;

    const draggableId = draggable.draggableId;
    const droppableId = droppable.droppableId;

    const draggingOver = this.findDraggingOver(draggableId, droppableId, state);
    const result: DragUpdate = {
      draggableId: draggableId,
      fromDroppableId: droppableId,
      toDroppableId: draggingOver === null ? null : draggingOver.droppableId,
      state: draggableStateFromMovableState(state)
    };

    onDragCancelCallback(result);
  };

  @action
  onDragEnd = (draggable: DraggableStore,
               droppable: DroppableStore,
               state: Readonly<MovableState>): void => {
    const draggableId = draggable.draggableId;
    const droppableId = droppable.droppableId;

    forEachDroppableStoreDesc(this.droppableStores,
      (droppableStore) =>
        droppableStore.setDraggingOverWith(null));

    const draggingOver = this.findDraggingOver(draggableId, droppableId, state);
    if (draggingOver !== null)
      draggingOver.setDraggingOverWith(draggableId);

    const onDragEndCallback = this.component.props.onDragEnd;

    const result: DragUpdate = {
      draggableId: draggableId,
      fromDroppableId: droppableId,
      toDroppableId: draggingOver === null ? null : draggingOver.droppableId,
      state: draggableStateFromMovableState(state)
    };

    onDragEndCallback(result);

    forEachDroppableStoreDesc(this.droppableStores, droppableStore => {
      droppableStore.setDraggingOverWith(null);
      droppableStore.setDraggingFromThisWith(null);
    });
  };

  private findDraggingOver = (draggableId: DraggableId,
                              source: DroppableId,
                              state: Readonly<MovableState>): DroppableStore | null => {
    let canDropOn = this.component.props.canDropOn;
    if (typeof canDropOn !== 'function')
      canDropOn = () => true;

    return findDraggingOverDesc(this.droppableStores, draggableId, source, canDropOn, state);
  };

  dispose = () => {
    this.disposers.dispose();
  };
}
